/**
 * Phase 6 — Admin observability tabs.
 * Used inside AdminConsole (Ratings + Timesheets tabs).
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { fmt } from "./_shared";


export function AdminRatingsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [relationship, setRelationship] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const qs = relationship ? `?relationship=${relationship}` : "";
      const r = await api(`/admin/ratings${qs}`);
      setRows(r.data || []);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [relationship]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <label className="text-sm">
          Filter by relationship:
          <select className="avcp-input ml-2" value={relationship}
                  onChange={(e) => setRelationship(e.target.value)}>
            <option value="">All</option>
            <option value="client_to_lead">Client → Lead</option>
            <option value="lead_to_helper">Lead → Helper</option>
            <option value="helper_to_lead">Helper → Lead</option>
          </select>
        </label>
      </div>
      <p className="text-xs text-[var(--avcp-ink-soft)] mb-3">
        Workforce Partners admins can see the rater identity for dispute investigation.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={TID.adminRatingsTable}>
          <thead>
            <tr className="text-left text-xs text-[var(--avcp-ink-soft)]">
              <th>When</th><th>Relationship</th><th>Booking</th>
              <th>Rater</th><th>Subject</th><th>Score</th><th>Comment</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="py-3">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="py-3 text-[var(--avcp-ink-soft)]">No ratings yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} data-testid={TID.adminRatingsRow}
                  className="border-t border-[var(--avcp-line)]">
                <td className="py-2">{fmt(r.created_at)}</td>
                <td>{r.relationship}</td>
                <td className="font-mono text-xs">#{r.booking_id}</td>
                <td>{r.rater_email}</td>
                <td>{r.subject_email}</td>
                <td>{r.score}★</td>
                <td className="max-w-xs truncate">{r.comment || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


export function AdminTimesheetsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const qs = state ? `?state=${state}` : "";
      const r = await api(`/admin/timesheets${qs}`);
      setRows(r.data || []);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [state]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <label className="text-sm">
          Filter by state:
          <select className="avcp-input ml-2" value={state}
                  onChange={(e) => setState(e.target.value)}>
            <option value="">All</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </label>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={TID.adminTimesheetsTable}>
          <thead>
            <tr className="text-left text-xs text-[var(--avcp-ink-soft)]">
              <th>When</th><th>Project</th><th>Party</th>
              <th>Outcome</th><th>Hours</th><th>State</th><th>Decided</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="py-3">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="py-3 text-[var(--avcp-ink-soft)]">No timesheets yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} data-testid={TID.adminTimesheetsRow}
                  className="border-t border-[var(--avcp-line)]">
                <td className="py-2">{fmt(r.submitted_at)}</td>
                <td>{r.job_title}</td>
                <td>{r.party_email} <span className="text-xs text-[var(--avcp-ink-soft)]">({r.party_kind})</span></td>
                <td>{r.outcome}</td>
                <td>{r.hours_worked}h</td>
                <td><span className="avcp-pill">{r.state}</span></td>
                <td>{fmt(r.decided_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
